import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Star, ChevronLeft, ChevronRight, Cpu, Radio, Sparkles, Shield, Calendar, Phone, Users, Eye, TrendingUp, UserPlus, QrCode, Wand2, Ticket } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import EliteSpotlight from "@/components/EliteSpotlight";
import ShareQrModal from "@/components/ShareQrModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_SLIDES = [
  { img: "https://images.unsplash.com/photo-1753105619712-4bf61810ef2a?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85", tag: "Luxury Weddings" },
  { img: "https://images.unsplash.com/photo-1664917303642-53aee32d3573?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85", tag: "Elite Galas" },
  { img: "https://images.pexels.com/photos/16935992/pexels-photo-16935992.jpeg?auto=compress&cs=tinysrgb&w=1920", tag: "Corporate Events" },
  { img: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1920&q=85", tag: "Private Celebrations" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.7, ease: [0.22, 1, 0.36, 1] } }),
};

/**
 * Fire-and-forget CTA click tracker. Uses sendBeacon when available so
 * the request survives the page navigation; falls back to fetch with
 * keepalive. Never throws — analytics must not block UX.
 *
 * Also drops a `cta_source` cookie (30-day, SameSite=Lax) so the
 * backend can attribute a future signup back to this click. The
 * /auth/register handler reads + clears the cookie, writing
 * `referred_by_cta` onto the new user record.
 */
function trackCtaClick(ctaId) {
  try {
    // 1) Attribution cookie — capped at 60 chars to match backend sanitizer.
    const safeId = String(ctaId).slice(0, 60);
    const expires = new Date(Date.now() + 30 * 24 * 3600 * 1000).toUTCString();
    document.cookie = `cta_source=${encodeURIComponent(safeId)}; expires=${expires}; path=/; SameSite=Lax`;

    // 2) Beacon log (survives the navigation about to happen).
    const payload = JSON.stringify({
      cta_id: ctaId,
      page: typeof window !== "undefined" ? window.location.pathname : "",
    });
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/track/cta-click`;
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
    } else {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // analytics must never break the click
  }
}

const EXPERIENCES = [
  { title: "Luxury Weddings", desc: "Cinematic documentation of your most extraordinary day. Every glance, every vow, immortalized.", img: "https://images.pexels.com/photos/35010559/pexels-photo-35010559.jpeg?auto=compress&cs=tinysrgb&w=800", span: "md:col-span-7 md:row-span-2" },
  { title: "Corporate Experiences", desc: "Product launches, galas, and retreats captured with editorial precision.", img: "https://images.pexels.com/photos/35042459/pexels-photo-35042459.jpeg?auto=compress&cs=tinysrgb&w=800", span: "md:col-span-5" },
  { title: "Private Elite Events", desc: "Intimate gatherings and exclusive parties documented with discretion.", img: "https://images.pexels.com/photos/29811325/pexels-photo-29811325.jpeg?auto=compress&cs=tinysrgb&w=800", span: "md:col-span-5" },
  { title: "Drone & Tech Integration", desc: "Aerial cinematography and smart coordination systems for unprecedented coverage.", img: "https://images.pexels.com/photos/8451502/pexels-photo-8451502.jpeg?auto=compress&cs=tinysrgb&w=800", span: "md:col-span-12" },
];

const TECH_FEATURES = [
  { icon: Cpu, title: "AI-Assisted Planning", desc: "Our ARIA concierge uses artificial intelligence to help plan, coordinate, and optimize every detail of your event." },
  { icon: Radio, title: "Drone Videography", desc: "Breathtaking aerial cinematography that captures the scale and grandeur of your event from perspectives never seen before." },
  { icon: Sparkles, title: "Smart Coordination", desc: "Real-time digital check-in, QR ticketing, live dashboards, and automated guest management powered by cutting-edge tech." },
  { icon: Shield, title: "Military-Grade Security", desc: "AES-256 encryption, secure client portals, and enterprise-level data protection for your most private moments." },
];

const PORTFOLIO_IMGS = [
  "https://images.unsplash.com/photo-1766393524464-e5eb1b05e4c8?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
  "https://images.unsplash.com/photo-1651667765471-5640f64bc679?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
  "https://images.unsplash.com/photo-1606030688449-d23c963c19ce?w=600&q=80",
  "https://images.unsplash.com/photo-1519741497674-611481863552?w=600&q=80",
  "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=600&q=80",
  "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&q=80",
];

export default function HomePage() {
  const [testimonials, setTestimonials] = useState([]);
  const [heroIdx, setHeroIdx] = useState(0);
  const [liveStats, setLiveStats] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [upcomingCount, setUpcomingCount] = useState(0);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then(res => setTestimonials(res.data)).catch(() => {});
    axios.get(`${API}/stats/live`).then(res => setLiveStats(res.data)).catch(() => {});
    axios.post(`${API}/track/visit`, { page: "/" }).catch(() => {});
    // Live count of upcoming ticketed events for the homepage Tickets tile.
    // Jamaica-local date (UTC-5) mirrors the filter on TicketsPage.js.
    axios.get(`${API}/events`).then(res => {
      const today = new Date(Date.now() - 5 * 60 * 60 * 1000)
        .toISOString().slice(0, 10);
      const n = (res.data || []).filter(e => e.event_date && e.event_date >= today).length;
      setUpcomingCount(n);
    }).catch(() => {});
  }, []);

  const nextSlide = useCallback(() => setHeroIdx(i => (i + 1) % HERO_SLIDES.length), []);
  const prevSlide = useCallback(() => setHeroIdx(i => (i - 1 + HERO_SLIDES.length) % HERO_SLIDES.length), []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const slide = HERO_SLIDES[heroIdx];

  return (
    <div data-testid="home-page">
      {/* ========== HERO ========== */}
      <section className="relative min-h-screen flex items-end pb-20 md:pb-28 overflow-hidden" data-testid="hero-section">
        <AnimatePresence mode="wait">
          <motion.div key={heroIdx} initial={{ opacity: 0, scale: 1.08 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }} className="absolute inset-0">
            <img src={slide.img} alt={slide.tag} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
          </motion.div>
        </AnimatePresence>

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 w-full">
          <AnimatePresence mode="wait">
            <motion.div key={heroIdx} initial="hidden" animate="visible" className="max-w-3xl">
              <motion.p variants={fadeUp} custom={0} className="text-xs sm:text-sm font-body font-medium uppercase tracking-[0.3em] text-gold mb-6">
                AristocratsJA Signature Events
              </motion.p>
              <motion.h1 variants={fadeUp} custom={1} className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-none text-white mb-6">
                Jamaica's Premier Signature<br /><span className="gold-gradient-text italic">Event Experience.</span>
              </motion.h1>
              <motion.p variants={fadeUp} custom={2} className="text-white/50 font-body font-light text-lg sm:text-xl leading-relaxed mb-10 max-w-xl">
                We don't just plan events. We engineer <em className="text-white/70 not-italic">unforgettable experiences.</em>
              </motion.p>
              <motion.div variants={fadeUp} custom={3} className="flex flex-wrap gap-4">
                <a href="https://wa.me/18764390976?text=Hi%20AristocratsJA!%20I'd%20like%20to%20book%20a%20consultation." target="_blank" rel="noopener noreferrer" data-testid="hero-cta-book" className="inline-flex items-center gap-2 bg-gold text-black font-body font-medium text-sm px-8 py-4 hover:bg-gold-light hover:scale-[1.03] transition-all">
                  Book Consultation <ArrowRight size={16} />
                </a>
                <Link to="/events" data-testid="hero-cta-experiences" className="inline-flex items-center gap-2 border border-white/20 text-white font-body font-light text-sm px-8 py-4 hover:border-gold/40 hover:text-gold transition-all">
                  View Experiences
                </Link>
              </motion.div>
            </motion.div>
          </AnimatePresence>

          <div className="absolute bottom-8 right-6 sm:right-12 flex items-center gap-3 z-20">
            <button onClick={prevSlide} data-testid="hero-prev" className="w-10 h-10 border border-white/15 flex items-center justify-center text-white/40 hover:text-gold hover:border-gold/40 transition-all"><ChevronLeft size={18} /></button>
            <div className="flex gap-2">
              {HERO_SLIDES.map((_, i) => (
                <button key={i} onClick={() => setHeroIdx(i)} data-testid={`hero-dot-${i}`} className={`h-1.5 rounded-full transition-all duration-500 ${i === heroIdx ? "bg-gold w-8" : "bg-white/15 w-1.5 hover:bg-white/30"}`} />
              ))}
            </div>
            <button onClick={nextSlide} data-testid="hero-next" className="w-10 h-10 border border-white/15 flex items-center justify-center text-white/40 hover:text-gold hover:border-gold/40 transition-all"><ChevronRight size={18} /></button>
          </div>
        </div>
      </section>

      {/* ========== LIVE PLATFORM STATS ========== */}
      <section className="py-10 sm:py-12 bg-[#050505] border-y border-white/5" data-testid="live-stats-bar">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          {liveStats ? (
            <>
              <div className="flex items-center justify-center gap-2 mb-6">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <p className="text-white/25 text-[10px] font-body uppercase tracking-[0.2em]">Live Platform Activity</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0} className="text-center p-4 sm:p-6 border border-white/5 bg-white/[0.015]">
                  <Eye size={18} className="text-gold/50 mx-auto mb-3" />
                  <p className="font-heading text-3xl sm:text-4xl text-white tracking-tight">{liveStats.visitors.total.toLocaleString()}</p>
                  <p className="text-white/25 font-body text-[10px] uppercase tracking-[0.12em] mt-1">Total Visitors</p>
                  <p className="text-gold/40 font-body text-[9px] mt-1">{liveStats.visitors.today} today</p>
                </motion.div>
                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1} className="text-center p-4 sm:p-6 border border-white/5 bg-white/[0.015]">
                  <TrendingUp size={18} className="text-gold/50 mx-auto mb-3" />
                  <p className="font-heading text-3xl sm:text-4xl text-white tracking-tight">{(liveStats.engagement.chats + liveStats.engagement.reviews + liveStats.engagement.checkins).toLocaleString()}</p>
                  <p className="text-white/25 font-body text-[10px] uppercase tracking-[0.12em] mt-1">Engagements</p>
                  <p className="text-gold/40 font-body text-[9px] mt-1">{liveStats.engagement.chats} conversations</p>
                </motion.div>
                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={2} className="text-center p-4 sm:p-6 border border-white/5 bg-white/[0.015]">
                  <Calendar size={18} className="text-gold/50 mx-auto mb-3" />
                  <p className="font-heading text-3xl sm:text-4xl text-white tracking-tight">{liveStats.services.total_bookings}</p>
                  <p className="text-white/25 font-body text-[10px] uppercase tracking-[0.12em] mt-1">Services Booked</p>
                  <p className="text-gold/40 font-body text-[9px] mt-1">{liveStats.services.confirmed} confirmed</p>
                </motion.div>
                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={3} className="text-center p-4 sm:p-6 border border-white/5 bg-white/[0.015]">
                  <UserPlus size={18} className="text-gold/50 mx-auto mb-3" />
                  <p className="font-heading text-3xl sm:text-4xl text-white tracking-tight">{(liveStats.accounts.total_users + liveStats.accounts.subscribers).toLocaleString()}</p>
                  <p className="text-white/25 font-body text-[10px] uppercase tracking-[0.12em] mt-1">Members</p>
                  <p className="text-gold/40 font-body text-[9px] mt-1">{liveStats.accounts.vip_clients} VIP clients</p>
                </motion.div>
              </div>
            </>
          ) : (
            <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4">
              {[
                { val: "500+", label: "Events Captured" },
                { val: "50+", label: "VIP Clients" },
                { val: "10+", label: "Years Excellence" },
                { val: "4.9", label: "Client Rating" },
              ].map((s, i) => (
                <motion.div key={s.label} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i} className="text-center px-4">
                  <p className="font-heading text-3xl sm:text-4xl text-gold tracking-tight">{s.val}</p>
                  <p className="text-white/30 font-body text-[10px] sm:text-xs uppercase tracking-[0.15em] mt-1">{s.label}</p>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ========== EXPERIENCES (SERVICES) ========== */}
      <section className="py-24 sm:py-32 bg-[#050505]" data-testid="experiences-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.2em] text-gold mb-4">Our Experiences</motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-white">Not Services. <span className="italic text-gold">Experiences.</span></motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 lg:gap-6">
            {EXPERIENCES.map((exp, i) => (
              <motion.div key={exp.title} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i} className={`${exp.span} group relative overflow-hidden ${i === 0 ? "min-h-[400px] md:min-h-[500px]" : "min-h-[240px] md:min-h-[240px]"}`}>
                <img src={exp.img} alt={exp.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                <div className="relative z-10 h-full flex flex-col justify-end p-6 sm:p-8">
                  <p className="text-gold text-[10px] font-body font-medium uppercase tracking-[0.2em] mb-2">{exp.title}</p>
                  <p className="text-white/60 font-body font-light text-sm leading-relaxed max-w-md">{exp.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services" data-testid="view-all-services" className="inline-flex items-center gap-2 text-gold text-sm font-body hover:text-gold-light transition-colors">
              Explore All Services <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ========== PORTFOLIO GALLERY ========== */}
      <section className="py-24 sm:py-32 bg-[#0F0F10]" data-testid="portfolio-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.2em] text-gold mb-4">Portfolio</motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-white">Not Every Event <span className="italic text-gold">Qualifies.</span></motion.h2>
          </motion.div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
            {PORTFOLIO_IMGS.map((src, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i} className="group relative aspect-[4/3] overflow-hidden">
                <img src={src} alt={`Portfolio ${i + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300" />
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/portfolio" className="inline-flex items-center gap-2 text-gold text-sm font-body hover:text-gold-light transition-colors">
              View Full Portfolio <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ========== TECHNOLOGY ADVANTAGE ========== */}
      <EliteSpotlight />

      <section className="py-24 sm:py-32 bg-[#050505] relative overflow-hidden" data-testid="tech-section">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gold/[0.03] rounded-full blur-[150px]" />
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.2em] text-gold mb-4">The Advantage</motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-white">Where Technology<br />Meets <span className="italic text-gold">Luxury.</span></motion.h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {TECH_FEATURES.map((feat, i) => (
              <motion.div key={feat.title} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i} className="backdrop-blur-xl bg-white/[0.02] border border-white/5 p-8 sm:p-10 group hover:border-gold/20 hover:-translate-y-1 transition-all duration-300">
                <feat.icon size={28} className="text-gold/70 mb-6 group-hover:text-gold transition-colors" strokeWidth={1.5} />
                <h3 className="font-heading text-xl sm:text-2xl font-light text-white mb-3 tracking-tight">{feat.title}</h3>
                <p className="text-white/40 font-body font-light text-sm leading-relaxed">{feat.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIALS ========== */}
      {testimonials.length > 0 && (
        <section className="py-24 sm:py-32 bg-[#0F0F10]" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
              <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.2em] text-gold mb-4">Testimonials</motion.p>
              <motion.h2 variants={fadeUp} custom={1} className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-white">Words from the <span className="italic text-gold">Elite.</span></motion.h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {testimonials.slice(0, 4).map((t, i) => (
                <motion.div key={t.id} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i} className="backdrop-blur-xl bg-white/[0.02] border border-white/5 p-8 sm:p-10 hover:border-gold/15 transition-colors">
                  <div className="flex gap-1 mb-5">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <Star key={j} size={14} className="text-gold fill-gold" />
                    ))}
                  </div>
                  <p className="text-white/60 font-body font-light text-sm sm:text-base leading-relaxed mb-6 italic">"{t.quote}"</p>
                  <div>
                    <p className="text-white text-sm font-body font-medium">{t.name}</p>
                    <p className="text-white/30 text-xs font-body mt-0.5">{t.role}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ========== PRICING ANCHOR ========== */}
      <section className="py-24 sm:py-32 bg-[#050505]" data-testid="pricing-section">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.2em] text-gold/60 mb-8">Investment</motion.p>
            <motion.div variants={fadeUp} custom={1} className="inline-block border border-gold/20 px-12 sm:px-20 py-12 sm:py-16 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-gold/[0.04] to-transparent" />
              <div className="relative z-10">
                <p className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-white tracking-tight mb-4">JMD <span className="text-gold">$150,000+</span></p>
                <p className="text-white/40 font-body font-light text-sm sm:text-base">Signature experiences starting at</p>
              </div>
            </motion.div>
            <motion.p variants={fadeUp} custom={2} className="text-white/25 font-body font-light text-xs mt-8 max-w-md mx-auto leading-relaxed">
              Every experience is bespoke. Final investment depends on scope, scale, and the level of luxury you envision.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ========== EXCLUSIVITY ========== */}
      <section className="py-28 sm:py-36 bg-[#0F0F10] relative overflow-hidden" data-testid="exclusivity-section">
        <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: "url('https://images.pexels.com/photos/7641151/pexels-photo-7641151.jpeg?auto=compress&cs=tinysrgb&w=1920')", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative z-10 max-w-4xl mx-auto px-6 sm:px-12 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.div variants={fadeUp} custom={0} className="flex items-center justify-center gap-3 mb-8">
              <div className="w-12 h-px bg-gold/30" />
              <Calendar size={16} className="text-gold/50" />
              <div className="w-12 h-px bg-gold/30" />
            </motion.div>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-white leading-none mb-6">
              Limited Bookings<br /><span className="italic text-gold">Per Month.</span>
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-white/35 font-body font-light text-base sm:text-lg leading-relaxed max-w-lg mx-auto mb-10">
              We accept a limited number of events each month to ensure every client receives our undivided attention and the highest level of excellence.
            </motion.p>
            <motion.a variants={fadeUp} custom={3} href="https://wa.me/18764390976?text=Hi%20AristocratsJA!%20I'd%20like%20to%20check%20availability." target="_blank" rel="noopener noreferrer" data-testid="exclusivity-cta" className="inline-flex items-center gap-2 border border-gold/30 text-gold font-body text-sm px-8 py-4 hover:bg-gold/10 hover:border-gold/50 transition-all">
              Check Availability <ArrowRight size={16} />
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* ========== AI EXPERIENCES SHOWCASE ========== */}
      <section
        className="py-24 sm:py-32 bg-[#050505] relative overflow-hidden border-t border-white/5"
        data-testid="ai-experiences-section"
      >
        <div className="absolute inset-0 opacity-[0.04]">
          <div className="absolute top-1/4 left-0 w-[400px] h-[400px] bg-gold rounded-full blur-[140px]" />
          <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] bg-gold rounded-full blur-[120px]" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-14 sm:mb-20">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.2em] text-gold mb-5">
              ✦ Powered by AI
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-white leading-none max-w-3xl">
              The future of luxury<br />events is <span className="italic text-gold">already here.</span>
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-white/35 font-body font-light text-base sm:text-lg leading-relaxed max-w-xl mt-6">
              Five proprietary AI tools that draft contracts, calculate budgets, generate posters, and elevate your photos to magazine-cover quality — instantly.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <motion.div variants={fadeUp} custom={3}>
              <Link
                to="/tickets"
                data-testid="home-tickets-tile"
                onClick={() => trackCtaClick("home-tickets-tile")}
                className="group block bg-[#0F0F10] border border-white/10 hover:border-gold/40 p-8 h-full transition-all relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-gold/[0.06] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative">
                  <Ticket size={28} className="text-gold mb-5" />
                  <p className="text-[10px] font-body font-medium uppercase tracking-[0.18em] text-gold/70 mb-2">
                    {upcomingCount > 0
                      ? `${upcomingCount} ${upcomingCount === 1 ? "event" : "events"} on sale`
                      : "Tickets"}
                  </p>
                  <h3 className="text-white font-heading text-2xl font-light mb-3">Upcoming Events</h3>
                  <p className="text-white/40 font-body font-light text-sm leading-relaxed mb-6">
                    Galas, weddings, and signature experiences. Pick a tier, check out in under two minutes.
                  </p>
                  <span data-testid="home-tickets-tile-cta" className="inline-flex items-center gap-1.5 text-gold text-xs font-body uppercase tracking-wider">
                    {upcomingCount > 0 ? "View tickets" : "Get notified"} <ArrowRight size={13} />
                  </span>
                </div>
              </Link>
            </motion.div>

            <motion.div variants={fadeUp} custom={4}>
              <Link
                to="/studio"
                data-testid="home-ai-studio-tile"
                onClick={() => trackCtaClick("home-ai-studio-tile")}
                className="group block bg-[#0F0F10] border border-white/10 hover:border-gold/40 p-8 h-full transition-all relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-violet-500/[0.04] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative">
                  <Wand2 size={28} className="text-violet-300 mb-5" />
                  <p className="text-[10px] font-body font-medium uppercase tracking-[0.18em] text-violet-300/70 mb-2">Public Tool</p>
                  <h3 className="text-white font-heading text-2xl font-light mb-3">AI Media Studio</h3>
                  <p className="text-white/40 font-body font-light text-sm leading-relaxed mb-6">
                    15 cinematic presets. GPT-5.2 scene detection. Cinema-grade colour grading on every photo you upload.
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-gold text-xs font-body uppercase tracking-wider">
                    Try the Studio <ArrowRight size={13} />
                  </span>
                </div>
              </Link>
            </motion.div>

            <motion.div variants={fadeUp} custom={5}>
              <Link
                to="/vip"
                data-testid="home-ai-toolkit-tile"
                onClick={() => trackCtaClick("home-ai-toolkit-tile")}
                className="group block bg-[#0F0F10] border border-gold/30 hover:border-gold/60 p-8 h-full transition-all relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-gold/[0.06] to-transparent opacity-100 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative">
                  <Sparkles size={28} className="text-gold mb-5" />
                  <p className="text-[10px] font-body font-medium uppercase tracking-[0.18em] text-gold/80 mb-2">VIP Exclusive · 5 tools</p>
                  <h3 className="text-white font-heading text-2xl font-light mb-3">AI Concierge Toolkit</h3>
                  <p className="text-white/40 font-body font-light text-sm leading-relaxed mb-6">
                    Budget calculator, contract drafter, style recommender, event poster generator, AI photo booth — all included.
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-gold text-xs font-body uppercase tracking-wider">
                    Sign up · Free <ArrowRight size={13} />
                  </span>
                </div>
              </Link>
            </motion.div>

            <motion.div variants={fadeUp} custom={6}>
              <Link
                to="/marketplace"
                data-testid="home-aria-agents-tile"
                onClick={() => trackCtaClick("home-aria-agents-tile")}
                className="group block bg-[#0F0F10] border border-white/10 hover:border-cyan-400/40 p-8 h-full transition-all relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/[0.04] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative">
                  <Cpu size={28} className="text-cyan-300 mb-5" />
                  <p className="text-[10px] font-body font-medium uppercase tracking-[0.18em] text-cyan-300/70 mb-2">Marketplace</p>
                  <h3 className="text-white font-heading text-2xl font-light mb-3">Agentic AI Freelancers</h3>
                  <p className="text-white/40 font-body font-light text-sm leading-relaxed mb-6">
                    ARIA-Engineer, ARIA-Writer, ARIA-Designer, ARIA-Analyst. Post a job, AI agents bid alongside human freelancers.
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-gold text-xs font-body uppercase tracking-wider">
                    Explore CartBlancJA <ArrowRight size={13} />
                  </span>
                </div>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="py-24 sm:py-32 bg-[#050505] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-gold/[0.04] rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] bg-gold/[0.02] rounded-full blur-[100px]" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 sm:px-12 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.2em] text-gold mb-6">Start Your Journey</motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-white leading-none mb-6">
              Ready to Create<br />Something <span className="italic text-gold">Extraordinary?</span>
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-white/40 font-body font-light text-base sm:text-lg leading-relaxed mb-10 max-w-lg mx-auto">
              Every detail matters. Every frame tells a story. Let us immortalize your most extraordinary moments.
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="flex flex-wrap justify-center gap-4">
              <a href="https://wa.me/18764390976?text=Hi%20AristocratsJA!%20I'm%20ready%20to%20start%20planning%20my%20event." target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp" className="inline-flex items-center gap-2 bg-gold text-black font-body font-medium text-sm px-10 py-4 hover:bg-gold-light hover:scale-[1.03] transition-all">
                <Phone size={16} /> Start on WhatsApp
              </a>
              <Link to="/booking" data-testid="cta-book-now" className="inline-flex items-center gap-2 border border-white/15 text-white font-body font-light text-sm px-10 py-4 hover:border-gold/30 hover:text-gold transition-all">
                Book Online <ArrowRight size={16} />
              </Link>
              <button onClick={() => setQrOpen(true)} data-testid="cta-share-qr" className="inline-flex items-center gap-2 border border-white/15 text-white font-body font-light text-sm px-10 py-4 hover:border-gold/30 hover:text-gold transition-all">
                <QrCode size={16} /> Share QR
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>
      <ShareQrModal open={qrOpen} onClose={() => setQrOpen(false)} />
    </div>
  );
}
